import api from './api';
import { ApiResponse } from '../types/api';
import { getCClassTribCbsIbsOptionsByCst } from './escritorioCbsIbsCclassOptions';

export interface Escritorio {
  id: number;
  identificacao: string;
  tipo: 'matriz' | 'filial';
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  fistel_anatel?: string | null;
  inscricao_estadual?: string | null;
  regime_tributario?: 'lucro_real' | 'lucro_presumido' | 'simples_nacional' | 'simples_nacional_excesso' | null;
  cep: string;
  codigo_municipio?: string;
  cidade: string;
  estado: string;
  endereco: string;
  bairro: string;
  numero?: string | null;
  complemento?: string | null;
  telefone?: string | null;
  email?: string | null;
  responsavel?: string | null;
  is_default: boolean;
  ativo: boolean;
  // Campos fiscais
  aliquota_icms?: number | string | null;
  aliquota_pis?: number | string | null;
  aliquota_cofins?: number | string | null;
  aliquota_fust?: number | string | null;
  aliquota_funttel?: number | string | null;
  aliquota_ibsuf?: number | string | null;
  aliquota_ibsmun?: number | string | null;
  aliquota_cbs?: number | string | null;
  cst_icms?: string | null;
  cst_pis_cofins?: string | null;
  cst_cbs_ibs?: string | null;
  c_class_trib_cbs_ibs?: string | null;
  msg_rodape?: string | null;
  descricao_padrao?: string | null;
  num_proxima_nota?: number;
  total_clientes_ativos?: number; // Contagem de clientes ativos
  certificado_digital_path?: string | null;
  certificado_digital_validade_ate?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface EscritorioFilters {
  page?: number;
  limit?: number;
  search?: string;
  tipo?: 'matriz' | 'filial' | 'todos';
  ativo?: 'true' | 'false' | 'todos';
  cidade?: string;
  estado?: string;
}

export interface EscritorioFormData {
  identificacao: string;
  tipo: 'matriz' | 'filial';
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  fistel_anatel?: string;
  inscricao_estadual?: string;
  regime_tributario?: 'lucro_real' | 'lucro_presumido' | 'simples_nacional' | 'simples_nacional_excesso';
  cep: string;
  codigo_municipio?: string;
  cidade: string;
  estado: string;
  endereco: string;
  bairro: string;
  numero?: string;
  complemento?: string;
  telefone?: string;
  email?: string;
  responsavel?: string;
  is_default?: boolean;
  // Campos fiscais
  aliquota_icms?: number | string;
  aliquota_pis?: number | string;
  aliquota_cofins?: number | string;
  aliquota_fust?: number | string;
  aliquota_funttel?: number | string;
  aliquota_ibsuf?: number | string;
  aliquota_ibsmun?: number | string;
  aliquota_cbs?: number | string;
  cst_icms?: string;
  cst_pis_cofins?: string;
  cst_cbs_ibs?: string;
  c_class_trib_cbs_ibs?: string;
  msg_rodape?: string;
  descricao_padrao?: string;
  num_proxima_nota?: number;
}

export interface EscritorioEstatisticas {
  totais: {
    total: number;
    ativos: number;
    inativos: number;
  };
  porTipo: Array<{
    tipo: string;
    total: number;
  }>;
  porEstado: Array<{
    estado: string;
    total: number;
  }>;
  escritorioDefault: Escritorio | null;
}

class EscritorioService {
  
  // Listar escritórios
  async getEscritorios(filters: EscritorioFilters = {}): Promise<ApiResponse<Escritorio[]> & { pagination?: { page: number; limit: number; total: number; pages: number; } }> {
    const response = await api.get('/escritorios', { params: filters });
    return response as unknown as ApiResponse<Escritorio[]> & { pagination?: { page: number; limit: number; total: number; pages: number; } };
  }

  // Buscar escritórios para select (rota otimizada)
  async getForSelect(params?: {
    ativo?: 'true' | 'false' | 'todos',
    limit?: number,
    search?: string,
    tipo?: 'matriz' | 'filial' | 'todos'
  }): Promise<ApiResponse<Escritorio[]>> {
    const response = await api.get<Escritorio[]>('/escritorios/select', { params });
    return response as unknown as ApiResponse<Escritorio[]>;
  }

  // Obter escritório por ID
  async getEscritorioById(id: number): Promise<ApiResponse<Escritorio>> {
    const response = await api.get(`/escritorios/${id}`);
    return response as unknown as ApiResponse<Escritorio>;
  }

  // Criar novo escritório
  async createEscritorio(data: EscritorioFormData): Promise<ApiResponse<Escritorio>> {
    const response = await api.post('/escritorios', data);
    return response as unknown as ApiResponse<Escritorio>;
  }

  // Atualizar escritório
  async updateEscritorio(id: number, data: Partial<EscritorioFormData>): Promise<ApiResponse<Escritorio>> {
    const response = await api.put(`/escritorios/${id}`, data);
    return response as unknown as ApiResponse<Escritorio>;
  }

  // Ativar/Desativar escritório
  async toggleEscritorioStatus(id: number): Promise<ApiResponse<Escritorio>> {
    const response = await api.patch(`/escritorios/${id}/toggle-status`);
    return response as unknown as ApiResponse<Escritorio>;
  }

  // Excluir escritório
  async deleteEscritorio(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete(`/escritorios/${id}`);
    return response as unknown as ApiResponse<void>;
  }

  // Obter escritório padrão
  async getEscritorioDefault(): Promise<ApiResponse<Escritorio>> {
    const response = await api.get('/escritorios/default');
    return response as unknown as ApiResponse<Escritorio>;
  }

  // Definir escritório como padrão
  async setAsDefault(id: number): Promise<ApiResponse<Escritorio>> {
    const response = await api.patch(`/escritorios/${id}/set-default`);
    return response as unknown as ApiResponse<Escritorio>;
  }

  // Obter estatísticas
  async getEstatisticas(): Promise<ApiResponse<EscritorioEstatisticas>> {
    const response = await api.get('/escritorios/stats');
    return response as unknown as ApiResponse<EscritorioEstatisticas>;
  }

  // Validar certificado digital (senha e validade)
  async validarCertificado(file: File, senha: string): Promise<ApiResponse<{ validade?: { notBefore: Date; notAfter: Date } }>> {
    const formData = new FormData();
    formData.append('certificado', file);
    formData.append('senha', senha);
    const response = await api.post('/escritorios/validar-certificado', formData);
    return response as unknown as ApiResponse<{ validade?: { notBefore: Date; notAfter: Date } }>;
  }

  // Upload certificado digital do escritório
  async uploadCertificado(escritorioId: number, file: File, senha: string): Promise<ApiResponse<{ validade?: { notBefore: Date; notAfter: Date } }>> {
    const formData = new FormData();
    formData.append('certificado', file);
    formData.append('senha', senha);
    const response = await api.post(`/escritorios/${escritorioId}/certificado-digital`, formData);
    return response as unknown as ApiResponse<{ validade?: { notBefore: Date; notAfter: Date } }>;
  }

  // Validar CNPJ
  validateCNPJ(cnpj: string): boolean {
    // Remove caracteres não numéricos
    const cleanCnpj = cnpj.replace(/\D/g, '');
    
    // Verifica se tem 14 dígitos
    if (cleanCnpj.length !== 14) {
      return false;
    }

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{13}$/.test(cleanCnpj)) {
      return false;
    }

    // Validação do primeiro dígito verificador
    let soma = 0;
    let peso = 2;
    for (let i = 11; i >= 0; i--) {
      soma += parseInt(cleanCnpj[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    let digito1 = soma % 11 < 2 ? 0 : 11 - (soma % 11);

    if (parseInt(cleanCnpj[12]) !== digito1) {
      return false;
    }

    // Validação do segundo dígito verificador
    soma = 0;
    peso = 2;
    for (let i = 12; i >= 0; i--) {
      soma += parseInt(cleanCnpj[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    let digito2 = soma % 11 < 2 ? 0 : 11 - (soma % 11);

    return parseInt(cleanCnpj[13]) === digito2;
  }

  // Validar CEP
  validateCEP(cep: string): boolean {
    const cleanCep = cep.replace(/\D/g, '');
    return cleanCep.length === 8;
  }

  // Formatar CNPJ
  formatCNPJ(cnpj: string): string {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    
    if (cleanCnpj.length === 14) {
      return cleanCnpj.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      );
    }
    
    return cnpj;
  }

  // Formatar CEP
  formatCEP(cep: string): string {
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length === 8) {
      return cleanCep.replace(/^(\d{5})(\d{3})$/, '$1-$2');
    }
    
    return cep;
  }

  // Formatar telefone
  formatTelefone(telefone: string): string {
    const cleanPhone = telefone.replace(/\D/g, '');
    
    if (cleanPhone.length === 11) {
      // Celular: (XX) XXXXX-XXXX
      return cleanPhone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    } else if (cleanPhone.length === 10) {
      // Fixo: (XX) XXXX-XXXX
      return cleanPhone.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    
    return telefone;
  }

  // Obter endereço completo formatado
  getEnderecoCompleto(escritorio: Escritorio): string {
    let endereco = `${escritorio.endereco}, ${escritorio.bairro}`;
    
    if (escritorio.numero) {
      endereco = `${escritorio.endereco}, ${escritorio.numero}, ${escritorio.bairro}`;
    }
    
    if (escritorio.complemento) {
      endereco += `, ${escritorio.complemento}`;
    }
    
    endereco += ` - ${escritorio.cidade}/${escritorio.estado}`;
    endereco += ` CEP: ${escritorio.cep}`;
    
    return endereco;
  }

  // Buscar CEP na API dos Correios
  async buscarCEP(cep: string): Promise<{
    success: boolean;
    data?: {
      cep: string;
      logradouro: string;
      complemento: string;
      bairro: string;
      localidade: string;
      uf: string;
    };
    error?: string;
  }> {
    try {
      const cleanCep = cep.replace(/\D/g, '');
      
      if (cleanCep.length !== 8) {
        return {
          success: false,
          error: 'CEP deve ter 8 dígitos'
        };
      }

      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        return {
          success: false,
          error: 'CEP não encontrado'
        };
      }

      return {
        success: true,
        data: {
          cep: data.cep,
          logradouro: data.logradouro,
          complemento: data.complemento,
          bairro: data.bairro,
          localidade: data.localidade,
          uf: data.uf
        }
      };
    } catch (error) {
      return {
        success: false,
        error: 'Erro ao buscar CEP'
      };
    }
  }

  // Lista de estados brasileiros
  getEstados(): Array<{ value: string; label: string }> {
    return [
      { value: 'AC', label: 'Acre' },
      { value: 'AL', label: 'Alagoas' },
      { value: 'AP', label: 'Amapá' },
      { value: 'AM', label: 'Amazonas' },
      { value: 'BA', label: 'Bahia' },
      { value: 'CE', label: 'Ceará' },
      { value: 'DF', label: 'Distrito Federal' },
      { value: 'ES', label: 'Espírito Santo' },
      { value: 'GO', label: 'Goiás' },
      { value: 'MA', label: 'Maranhão' },
      { value: 'MT', label: 'Mato Grosso' },
      { value: 'MS', label: 'Mato Grosso do Sul' },
      { value: 'MG', label: 'Minas Gerais' },
      { value: 'PA', label: 'Pará' },
      { value: 'PB', label: 'Paraíba' },
      { value: 'PR', label: 'Paraná' },
      { value: 'PE', label: 'Pernambuco' },
      { value: 'PI', label: 'Piauí' },
      { value: 'RJ', label: 'Rio de Janeiro' },
      { value: 'RN', label: 'Rio Grande do Norte' },
      { value: 'RS', label: 'Rio Grande do Sul' },
      { value: 'RO', label: 'Rondônia' },
      { value: 'RR', label: 'Roraima' },
      { value: 'SC', label: 'Santa Catarina' },
      { value: 'SP', label: 'São Paulo' },
      { value: 'SE', label: 'Sergipe' },
      { value: 'TO', label: 'Tocantins' }
    ];
  }

  // Opções de regime tributário
  getRegimesTributarios(): Array<{ value: string; label: string }> {
    return [
      { value: 'lucro_real', label: 'Lucro Real' },
      { value: 'lucro_presumido', label: 'Lucro Presumido' },
      { value: 'simples_nacional', label: 'Simples Nacional' },
      { value: 'simples_nacional_excesso', label: 'Simples Nacional - Excesso de Sublimite' }
    ];
  }

  // Opções de tipo de escritório
  getTiposEscritorio(): Array<{ value: string; label: string }> {
    return [
      { value: 'matriz', label: 'Matriz' },
      { value: 'filial', label: 'Filial' }
    ];
  }

  // CST ICMS para Regime Normal (Lucro Real, Lucro Presumido, Regime Normal)
  getCstIcmsRegimeNormal(): Array<{ value: string; label: string }> {
    return [
      { value: '00', label: '00 - Tributada integralmente' },
      { value: '10', label: '10 - Tributada e com cobrança do ICMS por substituição tributária' },
      { value: '10p', label: '10 - Tributada e com cobrança do ICMS por substituição tributária (com partilha do ICMS)' },
      { value: '20', label: '20 - Com redução de base de cálculo' },
      { value: '30', label: '30 - Isenta ou não tributada e com cobrança do ICMS por substituição tributária' },
      { value: '40', label: '40 - Isenta' },
      { value: '41', label: '41 - Não tributada' },
      { value: '41r', label: '41 - Não tributada (ICMS ST devido para a UF de destino)' },
      { value: '50', label: '50 - Suspensão' },
      { value: '51', label: '51 - Diferimento' },
      { value: '60', label: '60 - Cobrado anteriormente por substituição tributária' },
      { value: '70', label: '70 - Com redução de BC e cobrança do ICMS por substituição tributária' },
      { value: '90p', label: '90 - Outros (com partilha do ICMS)' },
      { value: '90', label: '90 - Outros' }
    ];
  }

  // CSOSN para Simples Nacional
  getCstIcmsSimplesNacional(): Array<{ value: string; label: string }> {
    return [
      { value: '101', label: '101 - Tributada com permissão de crédito' },
      { value: '102', label: '102 - Tributada sem permissão de crédito' },
      { value: '103', label: '103 - Isenção do ICMS para faixa de receita bruta' },
      { value: '201', label: '201 - Tributada com permissão de crédito e com cobrança do ICMS por ST' },
      { value: '202', label: '202 - Tributada sem permissão de crédito e com cobrança do ICMS por ST' },
      { value: '203', label: '203 - Isenção do ICMS para faixa de receita bruta e com cobrança do ICMS por ST' },
      { value: '300', label: '300 - Imune' },
      { value: '400', label: '400 - Não tributada pelo Simples Nacional' },
      { value: '500', label: '500 - ICMS cobrado anteriormente por ST ou por antecipação' },
      { value: '900', label: '900 - Outros' },
      { value: '90', label: '90 - Outros' }
    ];
  }

  // Retorna opções de CST ICMS baseado no regime tributário
  getCstIcmsOptions(regimeTributario?: string): Array<{ value: string; label: string }> {
    if (regimeTributario === 'simples_nacional') {
      return this.getCstIcmsSimplesNacional();
    }
    return this.getCstIcmsRegimeNormal();
  }

  // Opções de CST COFINS
  getCstCbsIbsOptions(): Array<{ value: string; label: string }> {
    return [
      { value: '000', label: '000 - Tributação integral' },
      { value: '010', label: '010 - Tributação com alíquotas uniformes' },
      { value: '011', label: '011 - Tributação com alíquotas uniformes reduzidas' },
      { value: '200', label: '200 - Alíquota zero' },
      { value: '220', label: '220 - Alíquota fixa' },
      { value: '221', label: '221 - Alíquota fixa proporcional' },
      { value: '222', label: '222 - Redução de Base de Cálculo' },
      { value: '400', label: '400 - Isenção' },
      { value: '410', label: '410 - Imunidade e não incidência' },
      { value: '510', label: '510 - Diferimento' },
      { value: '515', label: '515 - Diferimento com redução de alíquota' },
      { value: '550', label: '550 - Suspensão' },
      { value: '620', label: '620 - Tributação monofásica' },
      { value: '800', label: '800 - Transferência de crédito' },
      { value: '810', label: '810 - Ajuste de IBS na ZFM' },
      { value: '811', label: '811 - Ajustes' },
      { value: '820', label: '820 - Tributação em declaração de regime específico' },
      { value: '830', label: '830 - Exclusão de base de cálculo' },
    ];
  }

  /**
   * Opções de cClassTrib CBS/IBS por CST (expandir conforme tabelas oficiais).
   */
  getCClassTribCbsIbsOptions(cst: string | null | undefined): Array<{ value: string; label: string }> {
    const c = (cst || '').trim();
    return getCClassTribCbsIbsOptionsByCst(c);
  }

  getCstPisCofinsOptions(): Array<{ value: string; label: string }> {
    return [
      { value: '01', label: '01 - Operação Tributável com Alíquota Básica' },
      { value: '02', label: '02 - Operação Tributável com Alíquota Diferenciada' },
      { value: '03', label: '03 - Operação Tributável com Alíquota por Unidade de Medida de Produto' },
      { value: '04', label: '04 - Operação Tributável (Monofásica) - Revenda e Alíquota Zero' },
      { value: '05', label: '05 - Operação Tributável por Substituição Tributária' },
      { value: '06', label: '06 - Operação Tributável (Alíquota Zero)' },
      { value: '07', label: '07 - Operação Isenta da Contribuição' },
      { value: '08', label: '08 - Operação Sem Incidência da Contribuição' },
      { value: '09', label: '09 - Operação com Suspensão da Contribuição' },
      { value: '49', label: '49 - Outras Operações de Saída' },
      { value: '50', label: '50 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
      { value: '51', label: '51 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno' },
      { value: '52', label: '52 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita de Exportação' },
      { value: '53', label: '53 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
      { value: '54', label: '54 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
      { value: '55', label: '55 - Operação com Direito a Crédito - Vinculada a Receitas Não Tributadas no Mercado Interno e de Exportação' },
      { value: '56', label: '56 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno e de Exportação' },
      { value: '60', label: '60 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
      { value: '61', label: '61 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno' },
      { value: '62', label: '62 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita de Exportação' },
      { value: '63', label: '63 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
      { value: '64', label: '64 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
      { value: '65', label: '65 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação' },
      { value: '66', label: '66 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno e de Exportação' },
      { value: '67', label: '67 - Crédito Presumido - Outras Operações' },
      { value: '70', label: '70 - Operação de Aquisição sem Direito a Crédito' },
      { value: '71', label: '71 - Operação de Aquisição com Isenção' },
      { value: '72', label: '72 - Operação de Aquisição com Suspensão' },
      { value: '73', label: '73 - Operação de Aquisição a Alíquota Zero' },
      { value: '74', label: '74 - Operação de Aquisição sem Incidência da Contribuição' },
      { value: '75', label: '75 - Operação de Aquisição por Substituição Tributária' },
      { value: '98', label: '98 - Outras Operações de Entrada' },
      { value: '99', label: '99 - Outras Operações' }
    ];
  }

  // Formatar alíquota para exibição (valor direto de porcentagem)
  formatAliquota(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '';
    
    // Converter para número se for string
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    
    // Verificar se é um número válido
    if (isNaN(numericValue)) return '';
    
    return numericValue.toFixed(2) + '%';
  }

  // Converter entrada do usuário para valor direto de porcentagem
  parseAliquota(value: string): number | null {
    if (!value || value.trim() === '') return null;
    const cleanValue = value.replace(/[^\d.,]/g, '').replace(',', '.');
    const numericValue = parseFloat(cleanValue);
    if (isNaN(numericValue)) return null;
    return numericValue; // Mantém valor direto (ex: 19.00)
  }

  // Validar alíquota (deve estar entre 0 e 100%)
  validateAliquota(value: string): boolean {
    const parsed = this.parseAliquota(value);
    return parsed !== null && parsed >= 0 && parsed <= 100;
  }

  // Opções de descrição padrão
  getDescricaoPadraoOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'manter_existente', label: 'Manter a descrição existente do boleto' },
      { value: 'servico_comunicacao_multimidia', label: 'Serviço de Comunicação Multimidia' },
      { value: 'servico_comunicacao_multimidia_plano', label: 'Serviço de Comunicação Multimidia - {plano}' },
      { value: 'provimento_internet', label: 'Provimento de Internet' },
      { value: 'titulo_plano', label: 'Titulo do plano' }
    ];
  }
}

export const escritorioService = new EscritorioService();
export default escritorioService;
